import { pickLocalized, useLang, useT } from '../../i18n';
import type { HealthMetricTrend } from '../../types/health';
import MetricLineChart from './MetricLineChart';

interface MetricChartCardProps {
  series: HealthMetricTrend;
}

export default function MetricChartCard({ series }: MetricChartCardProps) {
  const t = useT();
  const { lang } = useLang();
  const points = [...series.points].sort((a, b) => Date.parse(a.date) - Date.parse(b.date));
  const latest = points[points.length - 1];
  const previous = points[points.length - 2];
  const delta = latest && previous ? latest.value - previous.value : null;

  return (
    <article className="rounded-xl border border-neutral-200 bg-white p-5 shadow-soft transition-shadow duration-200 hover:shadow-float">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-coffee/70">
            {t('health.chart.progress')}
          </p>
          <h4 className="mt-1 text-xl font-semibold text-cocoa">
            {pickLocalized(series.label, lang)}
          </h4>
          <p className="mt-1 text-xs font-mono text-coffee/70">
            {series.referenceRange ? `${t('health.chart.normalRange')} ${series.referenceRange}` : t('health.chart.noRange')}
          </p>
        </div>
        {latest && (
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-right">
            <p className="font-mono text-[10px] uppercase tracking-wider text-coffee/70">
              {t('health.chart.latest')}
            </p>
            <p className="text-lg font-semibold text-cocoa">
              {formatNumber(latest.value)}{series.unit ? ` ${series.unit}` : ''}
            </p>
          </div>
        )}
      </div>

      <div className="mt-4">
        {points.length === 0 ? (
          <div className="flex h-44 items-center justify-center rounded-lg border border-dashed border-neutral-300 bg-neutral-50 text-sm text-coffee/70">
            {t('health.chart.noData')}
          </div>
        ) : (
          <MetricLineChart series={{ ...series, points }} />
        )}
      </div>

      <footer className="mt-3 flex flex-wrap gap-3 text-xs font-mono text-coffee/70">
        <span>{t('health.chart.points', { count: points.length })}</span>
        {delta !== null && (
          <span>
            {t('health.chart.delta')} {delta > 0 ? '+' : ''}{formatNumber(delta)}{series.unit ? ` ${series.unit}` : ''}
          </span>
        )}
        {latest?.approximate && <span className="text-cocoa">{t('health.chart.approximate')}</span>}
      </footer>
    </article>
  );
}

function formatNumber(value: number): string {
  if (Math.abs(value) >= 100) return value.toFixed(0);
  if (Math.abs(value) >= 10) return value.toFixed(1);
  return value.toFixed(2);
}
